import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  avatarPresetIds,
  gauntletDefinitionIds,
  type GameInstance,
  type GameSubmissionInput,
  type GauntletSetup,
  type InvitationPreview,
  type Profile,
  type Session,
} from "@bluegreygames/contracts";
import { api, ApiError } from "./api";

const emptySubmission: GameSubmissionInput = {
  title: "",
  summary: "",
  instructions: "",
  durationMinutes: 15,
  location: "Main event area",
  materials: "",
  physicalDemands: "None",
  sensoryDemands: "None",
  socialDemands: "Small-group participation",
  costOrConsumption: "None",
  alternative: "Observe or assist without scoring penalty",
  scoring: "Moderator records the agreed result",
};

function errorMessage(error: unknown) {
  return error instanceof ApiError ? error.problem.detail : "Something went wrong. Try again.";
}

function Header({ session, onLogout }: { session: Session; onLogout: () => void }) {
  return <header className="site-header">
    <a className="brand" href="/" aria-label="Blue Grey Games portal home">
      <span className="brand-mark">BGG</span>
      <span>Blue Grey Games</span>
    </a>
    {session.authenticated && <button className="quiet" onClick={onLogout}>Sign out</button>}
  </header>;
}

function SignIn({ loginUrl }: { loginUrl: string }) {
  return <main className="center-panel">
    <p className="eyebrow">Private games, shared account</p>
    <h1>Your games, ready when your group is.</h1>
    <p>Create a Gauntlet, invite people you know, and keep setup and event-day state in one recoverable place.</p>
    <a className="primary-button" href={loginUrl}>Sign in or create an account</a>
    <p className="fine-print">Passwords, verification, and recovery are handled by Amazon Cognito. Joining an event is always a separate choice.</p>
  </main>;
}

function ProfileEditor({ initial, onSaved }: { initial: Profile | null; onSaved: (profile: Profile) => void }) {
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [avatarPresetId, setAvatarPresetId] = useState<Profile["avatarPresetId"]>(initial?.avatarPresetId ?? avatarPresetIds[0]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true); setError("");
    try {
      onSaved(await api.saveProfile({ displayName, avatarSetVersion: "shapes-v1", avatarPresetId }));
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setSaving(false); }
  }

  return <main className="content narrow">
    <p className="eyebrow">One quick step</p>
    <h1>Choose how your group sees you</h1>
    <p>Your display name and generated avatar are global. No photo upload is needed.</p>
    <form onSubmit={submit} className="stack">
      <label>Display name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={40} required /></label>
      <fieldset>
        <legend>Avatar</legend>
        <div className="avatar-grid">
          {avatarPresetIds.map((id) => <label className={`avatar-choice ${id === avatarPresetId ? "selected" : ""}`} key={id}>
            <input type="radio" name="avatar" value={id} checked={id === avatarPresetId} onChange={() => setAvatarPresetId(id)} />
            <img src={`/avatars/shapes-v1/${id}.svg`} alt="" />
            <span>{id.replaceAll("-", " ")}</span>
          </label>)}
        </div>
      </fieldset>
      {error && <p className="error" role="alert">{error}</p>}
      <button className="primary-button" disabled={saving}>{saving ? "Saving…" : "Save profile"}</button>
    </form>
  </main>;
}

function InvitationJoin({ token, authenticated, onAccepted }: {
  token: string; authenticated: boolean; onAccepted: (instanceId: string) => void;
}) {
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { api.previewInvitation(token).then(setPreview).catch((reason) => setError(errorMessage(reason))); }, [token]);

  if (error) return <main className="content narrow"><h1>Invitation unavailable</h1><p className="error">{error}</p></main>;
  if (!preview) return <main className="content"><p>Checking invitation…</p></main>;
  return <main className="content narrow">
    <p className="eyebrow">Gauntlet invitation</p>
    <h1>{preview.instanceName}</h1>
    <dl className="facts">
      <div><dt>Organizer</dt><dd>{preview.inviterDisplayName}</dd></div>
      <div><dt>Starts</dt><dd>{new Date(preview.startsAt).toLocaleString([], { timeZone: preview.timeZone })}</dd></div>
      <div><dt>Stage</dt><dd>External alpha</dd></div>
    </dl>
    <div className="notice"><strong>Before joining</strong><p>{preview.materialNotice}</p></div>
    {authenticated
      ? <button className="primary-button" onClick={() => api.acceptInvitation(token).then(({ instanceId }) => onAccepted(instanceId)).catch((reason) => setError(errorMessage(reason)))}>Accept invitation</button>
      : <a className="primary-button" href={`/auth/login?returnTo=${encodeURIComponent(`/join#token=${token}`)}`}>Sign in to accept</a>}
  </main>;
}

function CreateGame({ onCreated }: { onCreated: (game: GameInstance) => void }) {
  const tomorrow = new Date(Date.now() + 86_400_000);
  const [name, setName] = useState("My Gauntlet");
  const [startsAt, setStartsAt] = useState(tomorrow.toISOString().slice(0, 16));
  const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [responsibilityAccepted, setResponsibilityAccepted] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault(); setError("");
    try { onCreated(await api.createInstance({ name, startsAt: new Date(startsAt).toISOString(), timeZone, organizerResponsibilityAccepted: true })); }
    catch (reason) { setError(errorMessage(reason)); }
  }
  return <form className="card stack" onSubmit={submit}>
    <h2>Start a Gauntlet</h2>
    <label>Event name<input value={name} onChange={(event) => setName(event.target.value)} required /></label>
    <label>Start time<input type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} required /></label>
    <label>IANA time zone<input value={timeZone} onChange={(event) => setTimeZone(event.target.value)} required /></label>
    <label className="acknowledgement"><input type="checkbox" checked={responsibilityAccepted} onChange={(event) => setResponsibilityAccepted(event.target.checked)} required />
      <span>I accept responsibility as organizer for the event and all games or activities my group chooses. Blue Grey Games provides software tools and safety recommendations; it does not select, inspect, supervise, or enforce physical activities.</span>
    </label>
    {error && <p className="error" role="alert">{error}</p>}
    <button className="primary-button" disabled={!responsibilityAccepted}>Create private game</button>
  </form>;
}

function Dashboard({ profile, onOpen }: { profile: Profile; onOpen: (id: string) => void }) {
  const [games, setGames] = useState<GameInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => api.listInstances().then(({ items }) => setGames(items)).finally(() => setLoading(false)), []);
  useEffect(() => { void reload(); }, [reload]);
  return <main className="content">
    <section className="welcome">
      <img src={`/avatars/${profile.avatarSetVersion}/${profile.avatarPresetId}.svg`} alt="" />
      <div><p className="eyebrow">My Games</p><h1>Welcome, {profile.displayName}</h1></div>
    </section>
    <div className="dashboard-grid">
      <section>
        <h2>Current games</h2>
        {loading && <p>Loading…</p>}
        {!loading && games.length === 0 && <p className="empty">No games yet. Start one or open an invitation link.</p>}
        <div className="game-list">
          {games.map((game) => <button key={game.id} className="game-card" onClick={() => onOpen(game.id)}>
            <span className="status">{game.status}</span><strong>{game.name}</strong>
            <span>{new Date(game.startsAt).toLocaleString([], { timeZone: game.timeZone })}</span>
            <span>{game.role} · {game.nextAction}</span>
          </button>)}
        </div>
      </section>
      <CreateGame onCreated={(game) => { setGames((current) => [game, ...current]); onOpen(game.id); }} />
    </div>
  </main>;
}

function SubmissionForm({ onSave }: { onSave: (input: GameSubmissionInput) => Promise<void> }) {
  const [input, setInput] = useState(emptySubmission);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault(); setError("");
    try { await onSave(input); } catch (reason) { setError(errorMessage(reason)); }
  }
  const field = (key: keyof GameSubmissionInput, label: string, rows = 2) => <label>{label}<textarea rows={rows} value={String(input[key])} onChange={(event) => setInput({ ...input, [key]: event.target.value })} required /></label>;
  return <form className="card stack" onSubmit={submit}>
    <h2>Your game submission</h2>
    <label>Title<input value={input.title} onChange={(event) => setInput({ ...input, title: event.target.value })} required /></label>
    {field("summary", "Short summary")}{field("instructions", "Instructions", 4)}
    <label>Duration in minutes<input type="number" min={1} max={180} value={input.durationMinutes} onChange={(event) => setInput({ ...input, durationMinutes: Number(event.target.value) })} /></label>
    {field("location", "Location")}{field("materials", "Materials")}{field("physicalDemands", "Physical demands")}
    {field("sensoryDemands", "Sensory demands")}{field("socialDemands", "Social demands")}{field("costOrConsumption", "Cost or consumption")}
    {field("alternative", "Equivalent or neutral alternative")}{field("scoring", "Scoring and tie handling")}
    {error && <p className="error" role="alert">{error}</p>}
    <button className="primary-button">Submit immutable revision</button>
  </form>;
}

function GameSetup({ instanceId, onBack }: { instanceId: string; onBack: () => void }) {
  const [setup, setSetup] = useState<GauntletSetup | null>(null);
  const [error, setError] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const reload = useCallback(() => api.instanceSetup(instanceId).then(setSetup).catch((reason) => setError(errorMessage(reason))), [instanceId]);
  useEffect(() => { void reload(); }, [reload]);
  if (!setup) return <main className="content"><button className="back" onClick={onBack}>← My Games</button><p>{error || "Loading setup…"}</p></main>;
  const isHost = setup.instance.role === "host" || setup.instance.role === "cohost";
  return <main className="content">
    <button className="back" onClick={onBack}>← My Games</button>
    <div className="game-heading"><div><p className="eyebrow">Gauntlet · {setup.instance.status}</p><h1>{setup.instance.name}</h1></div><span className={`readiness ${setup.readiness.eligible ? "ready" : ""}`}>{setup.readiness.eligible ? "Ready to freeze" : `${setup.readiness.blockers.length} blockers`}</span></div>
    {error && <p className="error" role="alert">{error}</p>}
    <div className="setup-grid">
      <section className="card">
        <h2>Participants</h2>
        <ul className="member-list">{setup.members.map((member) => <li key={member.userId}><strong>{member.displayName}</strong><span>{member.role} · {member.submissionStatus} · {member.allocationId ?? "no identity"}</span></li>)}</ul>
        {isHost && <div className="stack"><button onClick={() => api.createInvitation(instanceId).then((result) => setShareUrl(result.shareUrl)).catch((reason) => setError(errorMessage(reason)))}>Create invitation link</button>{shareUrl && <label>Share this private link<input readOnly value={shareUrl} onFocus={(event) => event.currentTarget.select()} /></label>}</div>}
      </section>
      <section className="card">
        <h2>Gauntlet identity</h2>
        <p>Unique within this event. Abilities are not enabled in this slice.</p>
        <select defaultValue="" onChange={(event) => event.target.value && api.claimDefinition(instanceId, event.target.value).then(setSetup).catch((reason) => setError(errorMessage(reason)))}>
          <option value="" disabled>Choose an available identity</option>
          {gauntletDefinitionIds.map((id) => <option key={id} value={id} disabled={!setup.availableDefinitionIds.includes(id)}>{id}</option>)}
        </select>
      </section>
    </div>
    <SubmissionForm onSave={async (input) => setSetup(await api.saveSubmission(instanceId, input))} />
    {isHost && <section className="card"><h2>Submission review</h2><div className="submission-list">{setup.submissions.map((submission) => <article key={submission.id}><strong>{submission.title}</strong><p>{submission.summary}</p><span className="status">{submission.status}</span>{submission.status !== "approved" && <div><button onClick={() => api.reviewSubmission(instanceId, submission.id, { decision: "approved" }).then(setSetup)}>Approve revision</button><button className="quiet" onClick={() => api.reviewSubmission(instanceId, submission.id, { decision: "changes_requested", note: "Please clarify the demands and alternative." }).then(setSetup)}>Request changes</button></div>}</article>)}</div></section>}
    <section className="notice"><strong>Safer event recommendations</strong><ul><li>Review each game's physical, sensory, social, location, cost, and consumption demands before approval.</li><li>Offer a neutral alternative, make skipping penalty-free, and pause or stop when conditions change.</li><li>Plan for the venue, permissions, weather, transport, ages, alcohol policy, supervision, and emergencies.</li></ul><p>These are planning recommendations. The portal cannot observe or enforce conduct at the event.</p></section>
    {!setup.readiness.eligible && <section className="notice"><strong>Readiness blockers</strong><ul>{setup.readiness.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}</ul></section>}
  </main>;
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const hashToken = new URLSearchParams(window.location.hash.slice(1)).get("token");
  useEffect(() => { api.session().then((result) => { setSession(result); setProfile(result.user?.profile ?? null); }); }, []);
  if (!session) return <main className="content"><p>Loading portal…</p></main>;
  async function logout() { const { logoutUrl } = await api.logout(); window.location.assign(logoutUrl); }
  return <><Header session={session} onLogout={logout} />
    {hashToken
      ? <InvitationJoin token={hashToken} authenticated={session.authenticated} onAccepted={(id) => { history.replaceState(null, "", "/"); setInstanceId(id); }} />
      : !session.authenticated
        ? <SignIn loginUrl={session.loginUrl} />
        : !profile
          ? <ProfileEditor initial={null} onSaved={setProfile} />
          : instanceId
            ? <GameSetup instanceId={instanceId} onBack={() => setInstanceId(null)} />
            : <Dashboard profile={profile} onOpen={setInstanceId} />}
  </>;
}
